<template>
  <v-container fluid>
    <v-row v-if="loading">
      <v-col cols="12" style="padding: 60px 20px; text-align: center">
        <v-progress-circular indeterminate color="secondary" size="64"></v-progress-circular>
        <div class="text-h6 mt-4">Cargando asignaciones de equipo...</div>
      </v-col>
    </v-row>

    <v-container fluid v-else>
      <v-row align="center" no-gutters class="mb-4" justify="end">
        <v-col cols="auto">
          <v-btn class="secondary ui-module-btn-solid py-2 px-8" rounded elevation="8" @click="openCrearDialog()">
            Nueva asignación
            <v-icon small class="ml-2">mdi-plus</v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-data-table
            :headers="headers"
            :items="asignacionesTabla"
            class="primary ui-module-table"
            :loading="loading"
            no-data-text="No hay asignaciones registradas."
          >
            <template v-slot:[`item.fechaSolicitud`]="{ item }">
              <span>{{ formatearFecha(item.fechaSolicitud) }}</span>
            </template>
            <template v-slot:[`item.fechaAsignacion`]="{ item }">
              <span>{{ formatearFecha(item.fechaAsignacion) }}</span>
            </template>
            <template v-slot:[`item.cantidadEquipos`]="{ item }">
              <span>{{ (item.equipoAsignado || []).length }}</span>
            </template>
            <template v-slot:[`item.opciones`]="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    class="mr-1 ui-module-icon-action"
                    color="secondary"
                    @click="openDetalleDialog(item)"
                    v-on="on"
                    v-bind="attrs"
                  >
                    <v-icon>mdi-eye</v-icon>
                  </v-btn>
                </template>
                <span>Detalle de asignación</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    class="mr-1 ui-module-icon-action"
                    color="secondary"
                    @click="openEditarDialog(item)"
                    v-on="on"
                    v-bind="attrs"
                  >
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>Editar asignación</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    class="ui-module-icon-action"
                    color="secondary"
                    @click="openEliminarDialog(item)"
                    v-on="on"
                    v-bind="attrs"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span>Eliminar asignación</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-container>

    <asignacion-equipo-subsede-dialog-component
      :active="crearDialog || editarDialog"
      :subsedes="subsedes"
      :asignacion="selectedAsignacion"
      @close="closeFormDialogs()"
    ></asignacion-equipo-subsede-dialog-component>

    <eliminar-asignacion-equipo-subsede-dialog-component
      :active="eliminarDialog"
      :asignacion="selectedAsignacion"
      @close="closeEliminarDialog()"
    ></eliminar-asignacion-equipo-subsede-dialog-component>

    <detalle-asignacion-equipo-subsede-dialog-component
      :active="detalleDialog"
      :asignacion="selectedAsignacion"
      @close="closeDetalleDialog()"
    ></detalle-asignacion-equipo-subsede-dialog-component>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import AsignacionEquipoSubsedeDialogComponent from '../../components/equipamiento/AsignacionEquipoSubsedeDialogComponent.vue'
import EliminarAsignacionEquipoSubsedeDialogComponent from '../../components/equipamiento/EliminarAsignacionEquipoSubsedeDialogComponent.vue'
import DetalleAsignacionEquipoSubsedeDialogComponent from '../../components/equipamiento/DetalleAsignacionEquipoSubsedeDialogComponent.vue'

export default {
  components: {
    AsignacionEquipoSubsedeDialogComponent,
    EliminarAsignacionEquipoSubsedeDialogComponent,
    DetalleAsignacionEquipoSubsedeDialogComponent,
  },

  async created() {
    this.loading = true
    await Promise.all([this.fetchSubsedesAsignaciones(), this.fetchEquipo()])
    this.loading = false
  },

  data() {
    return {
      loading: false,

      headers: [
        { text: 'Subsede', value: 'subSede' },
        { text: 'Fecha de solicitud', value: 'fechaSolicitud' },
        { text: 'Fecha de asignación', value: 'fechaAsignacion' },
        { text: 'Folio de resguardo', value: 'folioResguardo' },
        { text: 'Equipos', value: 'cantidadEquipos' },
        { text: 'Opciones', value: 'opciones', sortable: false },
      ],

      crearDialog: false,
      editarDialog: false,
      eliminarDialog: false,
      detalleDialog: false,

      selectedAsignacion: null,
    }
  },

  computed: {
    ...mapGetters('subsedeAsignacion', { subsedes: 'getSubsedes' }),

    asignacionesTabla() {
      return (this.subsedes || []).flatMap((subsede) =>
        (subsede.asignaciones || [])
          .filter((asignacion) => (asignacion.equipoAsignado || []).length > 0)
          .map((asignacion) => ({
            ...asignacion,
            idSubSede: subsede.idSubSede,
            subSede: subsede.subSede,
            nombreSede: subsede.nombreSede,
          }))
      )
    },
  },

  methods: {
    ...mapActions('subsedeAsignacion', ['fetchSubsedesAsignaciones']),
    ...mapActions('equipo', ['fetchEquipo']),

    formatearFecha(valor) {
      if (!valor) return '—'
      const d = new Date(valor)
      return d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },

    openCrearDialog() {
      this.selectedAsignacion = null
      this.crearDialog = true
    },

    openEditarDialog(asignacion) {
      this.selectedAsignacion = asignacion
      this.editarDialog = true
    },

    closeFormDialogs() {
      this.crearDialog = false
      this.editarDialog = false
      this.selectedAsignacion = null
    },

    openEliminarDialog(asignacion) {
      this.selectedAsignacion = asignacion
      this.eliminarDialog = true
    },

    closeEliminarDialog() {
      this.eliminarDialog = false
      this.selectedAsignacion = null
    },

    openDetalleDialog(asignacion) {
      this.selectedAsignacion = asignacion
      this.detalleDialog = true
    },

    closeDetalleDialog() {
      this.detalleDialog = false
      this.selectedAsignacion = null
    },
  },
}
</script>
